import { z } from "zod";

export const MODEL_PROVIDER_CREDENTIAL_KIND = "model-provider-key" as const;
export const DOCUMENT_PARSER_CREDENTIAL_KIND = "document-parser-key" as const;
/**
 * A computer-use browser session (cookies + localStorage) persisted so a later
 * run resumes an authenticated site instead of logging in again. Derived auth
 * material, so it lives in Vault under the same encryption/RLS/BYOK-rewrap as a
 * password — but it is written and read only by the runner, never by a person,
 * which is why it is system-managed: no Vault UI row, no agent-visible entity,
 * no AOP `<credential>` grant, and the /internal/dashboard/vault write routes refuse the kind.
 */
export const BROWSER_SESSION_CREDENTIAL_KIND = "browser-session" as const;

export const SYSTEM_MANAGED_VAULT_CREDENTIAL_KINDS = [
  "outbound-oauth-client",
  "workspace-github-user",
  MODEL_PROVIDER_CREDENTIAL_KIND,
  DOCUMENT_PARSER_CREDENTIAL_KIND,
  BROWSER_SESSION_CREDENTIAL_KIND,
] as const;

export type SystemManagedVaultCredentialKind =
  (typeof SYSTEM_MANAGED_VAULT_CREDENTIAL_KINDS)[number];

export function isSystemManagedVaultCredentialKind(
  kind: string,
): kind is SystemManagedVaultCredentialKind {
  return (SYSTEM_MANAGED_VAULT_CREDENTIAL_KINDS as readonly string[]).includes(kind);
}

/**
 * Provider credentials are references, never secret values. Deployment
 * references resolve through the customer cloud's secret store; workspace
 * references resolve through the system-managed provider credential store in
 * the owning workspace. Quovy Vault is its internal encryption backend.
 */
export const providerCredentialRefSchema = z.discriminatedUnion("source", [
  z.object({ source: z.literal("none") }),
  z.object({ source: z.literal("ambient") }),
  z.object({
    source: z.literal("workspace-vault"),
    credentialId: z.string().min(1),
  }),
  z.object({
    source: z.literal("aws-secrets-manager"),
    secretId: z.string().min(1),
    region: z.string().min(1).optional(),
  }),
  z.object({
    source: z.literal("azure-key-vault"),
    vaultUrl: z
      .string()
      .url()
      .refine((value) => value.startsWith("https://"), {
        message: "Azure Key Vault URLs must use HTTPS",
      }),
    secretName: z.string().min(1),
  }),
]);

export type ProviderCredentialRef = z.infer<typeof providerCredentialRefSchema>;

export type ProviderConnectionOrigin = "environment" | "deployment" | "workspace";

export function providerCredentialScope(
  credential: ProviderCredentialRef,
): "none" | "deployment" | "workspace" {
  switch (credential.source) {
    case "none":
      return "none";
    case "workspace-vault":
      return "workspace";
    case "ambient":
    case "aws-secrets-manager":
    case "azure-key-vault":
      return "deployment";
  }
}
